import moment from 'moment'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { utils, writeFile } from 'xlsx'
import { Workbook } from 'exceljs'
import { InventoryItemDto } from '../interfaces/Inventory/InventoryItemDto'
import logo from '../assets/images/TEALCA-logo.jpeg'
import {
  UserInterface,
  ShipmentInterface,
  DestinationInterface,
  DispatcherCCWInterface,
  FavoriteDestinationsInterfase,
  StoreShipmentInterface,
  PieceCategoryInterface,
  DeliveryEnum,
} from '../interfaces'
import { shipmentStatusFormat } from './format'

const toSentenceCase = (str: string) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const toTitleCase = (str: string) => {
  if (!str) return ''
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const exportDestinationsPDF = (
  user: UserInterface,
  destinations: DestinationInterface[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE', 87, 20)
  doc.text('DESTINATARIO', 84, 26)

  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Código destinatario',
    'Alias',
    'Ciudad',
    'Teléfono',
    'Tienda',
    'Persona de accounto',
  ]
  const rows = destinations.map((destination) => [
    destination.location.code ?? '',
    destination.location.name,
    'CCS',
    destination.account.listAccountPhone[0]?.phoneNumber ?? '',
    destination.businessUnit?.buCode,
    destination.account.accountFullName,
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 40, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.pdf`)
}

export const exportDestinationsXLSX = (
  destinations: DestinationInterface[]
) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = destinations.map((destination) => [
    destination.location.code,
    destination.location.name,
    'CCS',
    destination.account.listAccountPhone[0]?.phoneNumber ?? '',
    destination.businessUnit?.buCode,
    destination.account.accountFullName,
  ])

  // Add header
  ws_data.unshift([
    'Código destinatario',
    'Alias',
    'Ciudad',
    'Teléfono',
    'Tienda',
    'Persona de accounto',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportDestinationsTXT = (destinations: DestinationInterface[]) => {
  let txt = ''

  for (let i = 0; i < destinations.length; i++) {
    const destination = destinations[i]
    const identificationType = destination.account.abbreviationName

    txt += `"DestinationId":"${destination.id}",\n`
    txt += `"DestinationCod":"${destination.location.code}",\n`
    txt += `"Description":"${destination.location.name}",\n`
    txt += `"Alias":"${destination.location.name}",\n`
    txt += `"AddressFull":"${destination.location.address}",\n`
    txt += `"City":"CCS",\n`
    txt += `"Country":"VZL",\n`
    txt += `"Phone":"${destination.account.listAccountPhone[0]?.phoneNumber}",\n`
    txt += `"Phone2":"${destination.account.listAccountPhone[1]?.phoneNumber}",\n`
    txt += `"Email":"${destination.account.listAccountEmail[0]?.email}",\n`
    txt += `"OfficeCod":"",\n`
    txt += `"NameContact":"${destination.account.accountFullName}",\n`
    txt += `"DestinationDocument":"${identificationType}${destination.account.identificationNumber}",\n`
    txt += `"DocumentType":"${identificationType.slice(0, 1)}",\n`
    txt += `"NameContact":"${destination.account.identificationNumber}"`
    txt += i < destinations.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

//////////Export Shipments

export const exportShipmentsPDF = (
  user: UserInterface,
  shipments: StoreShipmentInterface[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE ENVIO', 80, 25)
  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Guía',
    'Fecha de Creación',
    'Nº de Piezas',
    'Peso',
    'Nombre Destinatario',
    'Teléfono',
    'Ciudad',
    'Entrega',
    'Guía Aliada',
    'Estatus',
  ]
  const rows = shipments.map((shipment) => [
    shipment.shipmentNumber,
    moment(shipment.creationDate).format('DD/MM/YYYY'),
    shipment.totalPieces ?? 0,
    shipment.totalChargedWeight ?? 0,
    toTitleCase(shipment.consigneeName),
    shipment.consigneePhone ?? '',
    shipment.locationName ?? '',
    shipment.storePickup === '1'
      ? (shipment.buCodeConsignee || '') +
        ' - ' +
        toSentenceCase(shipment.consigneeBUAreaName || '')
      : 'A domicilio',
    shipment.shipmentReference ?? '',
    toSentenceCase(shipmentStatusFormat(shipment?.statusID)),
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 45, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
      minCellHeight: 11,
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaGuia_${moment().format('DDMMYYYYHHmm')}.pdf`)
}

export const exportShipmentsXLSX = (shipments: StoreShipmentInterface[]) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = shipments.map((shipment) => [
    shipment.shipmentNumber,
    moment(shipment.creationDate).format('DD/MM/YYYY'),
    shipment.totalPieces ?? 0,
    shipment.totalChargedWeight ?? 0,
    toTitleCase(shipment.consigneeName),
    shipment.consigneePhone ?? '',
    shipment.locationName ?? '',
    shipment.storePickup === '1'
      ? (shipment.buCodeConsignee || '') +
        ' - ' +
        toSentenceCase(shipment.consigneeBUAreaName || '')
      : 'A domicilio',
    shipment.shipmentReference ?? '',
    toSentenceCase(shipmentStatusFormat(shipment?.statusID)),
  ])

  // Add header
  ws_data.unshift([
    'Guía',
    'Fecha de Creación',
    'Nº de Piezas',
    'Peso',
    'Nombre Destinatario',
    'Teléfono',
    'Ciudad',
    'Entrega',
    'Guía Aliada',
    'Estatus',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaGuia_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportShipmentsTXT = (shipments: StoreShipmentInterface[]) => {
  let txt = ''

  for (let i = 0; i < shipments.length; i++) {
    const shipment = shipments[i]

    txt += `"GUÍA":"${shipment.shipmentNumber}",\n`
    txt += `"Nº de Piezas":${shipment.totalPieces},\n`
    txt += `"Peso":${shipment.totalChargedWeight},\n`
    txt += `"Nombre Destinatario":"${toTitleCase(shipment.consigneeName)}",\n`
    txt += `"Teléfono":"${shipment.consigneePhone}",\n`
    txt += `"Ciudad":"${shipment.locationName}",\n`
    txt += `"Entrega":"${
      shipment.storePickup === '1'
        ? (shipment.buCodeConsignee?.toUpperCase() || '') +
          ' - ' +
          toSentenceCase(shipment.consigneeBUAreaName?.toUpperCase() || '')
        : 'A domicilio'
    }",\n`
    txt += `"Guía Aliada":"${shipment.shipmentReference}",\n`
    txt += `"Estatus":"${toSentenceCase(
      shipmentStatusFormat(shipment?.statusID)
    )}",\n`
    txt += `"Fecha de Creación":"${moment(shipment.creationDate).format(
      'DD/MM/YYYY'
    )}"`
    txt += i < shipments.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaGuia_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

// ---------------------------------------------------------------------------
// Export genérico de tablas (reutilizable por cualquier módulo). `columns` son los
// encabezados; `rows` una matriz de celdas (cada fila con el mismo largo que columns).
// Mantiene el estilo de los demás reportes: logo, título, fecha y footer con usuario
// y nº de página. Lo usan los listados de importación masiva (list y detalle).
// ---------------------------------------------------------------------------
export const exportTablePDF = (
  user: UserInterface,
  title: string,
  columns: string[],
  rows: (string | number)[][],
  filePrefix: string
) => {
  const doc = new jsPDF(columns.length > 6 ? 'landscape' : 'portrait')
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()

  const img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text(title, pageW / 2, 22, { align: 'center' })

  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), pageW - 40, 20)
  doc.text(date.format('HH:mm'), pageW - 32, 24)

  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 40, bottom: 18 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: { fillColor: [0, 100, 172], textColor: 255, fontStyle: 'bold' },
    bodyStyles: { minCellHeight: 10, lineWidth: 0.1, textColor: 'black' },
  })

  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.text(`Solicitado por: ${user.login}`, 13, pageH - 8)
    doc.text(`${i}`, pageW - 15, pageH - 8)
  }

  doc.save(`${filePrefix}_${moment().format('DDMMYYYYHHmm')}.pdf`)
}

export const exportTableXLSX = (
  columns: string[],
  rows: (string | number)[][],
  sheetName: string,
  filePrefix: string
) => {
  const wb = utils.book_new()
  const ws = utils.aoa_to_sheet([columns, ...rows])
  utils.book_append_sheet(wb, ws, sheetName.slice(0, 31))
  writeFile(wb, `${filePrefix}_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportTableTXT = (
  columns: string[],
  rows: (string | number)[][],
  filePrefix: string
) => {
  let txt = ''
  rows.forEach((row, i) => {
    row.forEach((cell, j) => {
      const sep = j < row.length - 1 ? ',' : ''
      txt += `"${columns[j]}":"${cell ?? ''}"${sep}\n`
    })
    txt += i < rows.length - 1 ? '\n' : '\n\n'
  })

  const element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `${filePrefix}_${moment().format('DDMMYYYYHHmm')}.txt`
  )
  element.style.display = 'none'
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)
}

///////////// Export Dispatch
export const exportDispatchPDF = (
  user: UserInterface,
  dispatchs: DispatcherCCWInterface[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE DESPACHO', 80, 25)
  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Codigo ',
    'Origen',
    'Destino',
    'Transporte',
    'Conductor',
    'Precinto 1',
    'Precinto 2',
    'Precinto 3',
  ]
  const rows = dispatchs.map((dispatch) => [
    dispatch.masterShipmentHeaderCode,
    dispatch.buCodeSource,
    dispatch.buCodeDestination,
    dispatch.transport,
    dispatch.brandName,
    dispatch.seal1 ?? 'N/D',
    dispatch.seal2 ?? 'N/D',
    dispatch.seal3 ?? 'N/D',
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 45, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
      minCellHeight: 11,
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaDespacho_${moment().format('DDMMYYYYHHmm')}.pdf`)
}

export const exportDispatchXLSX = (dispatchs: DispatcherCCWInterface[]) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = dispatchs.map((dispatch) => [
    dispatch.masterShipmentHeaderCode,
    dispatch.buCodeSource,
    dispatch.buCodeDestination,
    dispatch.transport,
    dispatch.brandName,
    dispatch.seal1 ?? 'N/D',
    dispatch.seal2 ?? 'N/D',
    dispatch.seal3 ?? 'N/D',
  ])

  // Add header
  ws_data.unshift([
    'Codigo ',
    'Origen',
    'Destino',
    'Transporte',
    'Conductor',
    'Precinto 1',
    'Precinto 2',
    'Precinto 3',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaDespacho_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportDispatchTXT = (dispatchs: DispatcherCCWInterface[]) => {
  let txt = ''

  for (let i = 0; i < dispatchs.length; i++) {
    const shipment = dispatchs[i]

    txt += `"Codigo":"${shipment.masterShipmentHeaderCode}",\n`
    txt += `"Origen":${shipment.buCodeSource},\n`
    txt += `"Destino":${shipment.buCodeDestination},\n`
    txt += `"Transporte":"${shipment.transport}",\n`
    txt += `"Conductor":"${shipment.brandName}",\n`
    txt += `"Precinto 1":"${shipment.seal1 ?? 'N/D'}",\n`
    txt += `"Precinto 2":"${shipment.seal2 ?? 'N/D'}",\n`
    txt += `"Precinto 3":"${shipment.seal3 ?? 'N/D'}"`
    txt += i < dispatchs.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

export const exportFormatoShipmentXLSX = () => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = []

  // Add header
  ws_data.unshift([
    'Fecha de Creación',
    'Numero de Guía / Tracking aliado',
    'Tipo de retiro',
    'Ciudad destino',
    'Codigo Tienda destinatario',
    'Tipo de documento Remitente',
    'Nº de Documento Remitente',
    'Nombre Remitente',
    'Tipo de documento destinatario',
    'Nº de Documento destinatario',
    'Nombre destinatario',
    'Telefono destinatario',
    'Correo destinatario',
    'Dirección Destino',
    'Codigo Postal',
    'Observaciones',
    'Nº Pieza',
    'Peso en Kg de la pieza',
    'Dimension Alto de la pieza',
    'Dimension Anchode la pieza',
    'Dimension Largo de la pieza',
    'Valor declarado de la pieza',
    'Categoria de la pieza',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'FormatoMasivo')

  // Export
  writeFile(wb, `FormatoCargaMasiva_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

// Export format for Inventory shipments template download
export const exportFormatoShipmentInventoryXLSX = async (
  identificationTypes: {
    taxIdentificationTypeId: string | number
    abbreviationName: string
  }[],
  typeShipment: { value: string | number; name: string }[],
  businessUnits: { buCode: string; buName?: string; buArea?: string }[],
  citiesType: { locationCode: string; locationName: string }[],
  products?: InventoryItemDto[]
) => {
  // Build workbook with ExcelJS to support data validation lists
  const wb = new Workbook()
  const ws = wb.addWorksheet('FormatoInventario')

  // Hidden sheet with lists (referencia y ayuda)
  const lists = wb.addWorksheet('Listas')
  lists.getCell('A1').value = 'CodigoProducto'
  lists.getCell('B1').value = 'NombreProducto'
  const genericProducts =
    products && products.length > 0
      ? products.map((p) => ({ code: p.Id, name: p.Name }))
      : []

  genericProducts.forEach((p, idx) => {
    lists.getCell(`A${idx + 2}`).value = p.code
    lists.getCell(`B${idx + 2}`).value = p.name
  })
  // Asegurar que los códigos de producto se traten como texto (preserva ceros a la izquierda)
  lists.getColumn(1).numFmt = '@'
  // (El rango se referencia directamente en la fórmula usando columnas completas A:B para simplificar)

  // Columna para tipos de documento (ID y Abrev)
  lists.getCell('D1').value = 'TaxIdentificationTypeId'
  lists.getCell('E1').value = 'Abreviacion'
  identificationTypes.forEach((t, idx) => {
    lists.getCell(`D${idx + 2}`).value = t.taxIdentificationTypeId
    lists.getCell(`E${idx + 2}`).value = t.abbreviationName
  })

  // Tipos de entrega
  lists.getCell('G1').value = 'TipoEntregaValor'
  lists.getCell('H1').value = 'TipoEntregaNombre'
  typeShipment.forEach((t, idx) => {
    lists.getCell(`G${idx + 2}`).value = t.value
    lists.getCell(`H${idx + 2}`).value = t.name
  })

  // Tiendas
  lists.getCell('J1').value = 'BuCodeArea'
  lists.getCell('K1').value = 'BuName'
  businessUnits
    .sort((a, b) => (a.buCode || '').localeCompare(b.buCode || ''))
    .forEach((b, idx) => {
      lists.getCell(`J${idx + 2}`).value = `${b.buCode} - ${
        b.buArea?.toUpperCase() || b.buCode?.toUpperCase()
      }`
      lists.getCell(`K${idx + 2}`).value = b.buName || ''
    })

  // Ciudades
  lists.getCell('M1').value = 'CityCode'
  lists.getCell('N1').value = 'CityName'
  citiesType.forEach((c, idx) => {
    lists.getCell(`M${idx + 2}`).value = c.locationCode
    lists.getCell(`N${idx + 2}`).value = c.locationName
  })

  lists.state = 'hidden'

  // Header row
  const headers = [
    'Número de pedido',
    'Tipo de documento destinatario',
    'Número de documento destinatario',
    'Nombre destinatario',
    'Teléfono destinatario',
    'Correo destinatario',
    'Tipo de entrega (retiro por oficina/domicilio)',
    'Código de tienda destino (si es retiro por oficina)',
    'Ciudad/Localidad destino (si es domicilio)',
    'Dirección destino (si es domicilio)',
    'Código postal destino (opcional)',
    'Observaciones (opcional)',
    'Nombre producto',
    'Código producto',
    'Cantidad',
  ]
  ws.addRow(headers)
  ws.getRow(1).font = { bold: true }
  // Columna M (nombre de producto) y N (código de producto) como texto
  ws.getColumn(13).numFmt = '@'
  ws.getColumn(14).numFmt = '@'
  // Add helpful notes to headers
  ws.getCell('B1').note =
    'Seleccione tipo de documento. Se almacenará la abreviación; al importar se mapeará al ID.'
  ws.getCell('G1').note = 'Seleccione tipo de entrega.'
  ws.getCell('H1').note = 'Seleccione código de tienda destino.'
  ws.getCell('I1').note = 'Seleccione ciudad/localidad destino.'
  ws.getCell('M1').note =
    'Seleccione nombre de producto de la lista desplegable.'
  ws.getCell('N1').note =
    'Esta celda se llena automáticamente basado en el nombre de producto seleccionado.'

  // Apply data validation lists over a reasonable number of rows (2..1000)
  // Helper para construir listas en línea (Excel límite ~255 chars)
  const buildInlineList = (values: (string | number)[]) => {
    const clean = values
      .map((v) => (v ?? '').toString().trim())
      .filter((v) => v.length > 0 && !v.includes(','))
    const joined = clean.join(',')
    if (!joined) return null
    if (joined.length <= 255) return `"${joined}"`
    return null // demasiado largo, se omitirá la validación
  }

  const listIdentAbrev = buildInlineList(
    identificationTypes.map((t) => t.abbreviationName)
  )
  const listTipoEntrega = buildInlineList(typeShipment.map((t) => t.name))
  const listTiendas = buildInlineList(
    businessUnits
      .sort((a, b) => (a.buCode || '').localeCompare(b.buCode || ''))
      .map((b) => `${b.buCode} - ${b.buArea || b.buCode}`)
  )
  const listCiudades = buildInlineList(citiesType.map((c) => c.locationName))

  // Rango de respaldo (fallback) para tiendas y ciudades cuando exceden 255 caracteres
  const tiendasRange = businessUnits.length
    ? `=Listas!$J$2:$J$${businessUnits.length + 1}`
    : ''
  const ciudadesRange = citiesType.length
    ? `=Listas!$N$2:$N$${citiesType.length + 1}`
    : ''
  const productosRange = genericProducts.length
    ? `=Listas!$A$2:$A$${genericProducts.length + 1}`
    : ''
  const productosNombresRange = genericProducts.length
    ? `=Listas!$B$2:$B$${genericProducts.length + 1}`
    : ''

  for (let r = 2; r <= 1000; r++) {
    if (listIdentAbrev) {
      ws.getCell(`B${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [listIdentAbrev],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Tipo documento',
        prompt: 'Seleccione una abreviación',
        errorStyle: 'warning',
        error: 'Valor no válido.',
      }
    }
    if (listTipoEntrega) {
      ws.getCell(`G${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [listTipoEntrega],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Tipo de entrega',
        prompt: 'Seleccione opción',
        errorStyle: 'stop',
        error: 'Valor no válido.',
      }
    }
    if (listTiendas || tiendasRange) {
      ws.getCell(`H${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [
          `=IF(G${r}="Oficina", ${
            listTiendas ? listTiendas : tiendasRange.replace('=', '')
          }, "")`,
        ],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Código tienda',
        prompt: 'Seleccione buCode si retiro por oficina',
        errorStyle: 'stop',
        error: 'Opción no válida, Debe seleccionar tipo de entrega Oficina',
      }
    }
    if (listCiudades || ciudadesRange) {
      ws.getCell(`I${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [
          `=IF(G${r}="Domicilio", ${
            listCiudades ? listCiudades : ciudadesRange.replace('=', '')
          }, "")`,
        ],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Ciudad',
        prompt: 'Seleccione ciudad destino si domicilio',
        errorStyle: 'stop',
        error: 'Opción no válida, Debe seleccionar tipo de entrega Domicilio',
      }
    }
    // Dirección destino solo si Domicilio
    ws.getCell(`J${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [`=OR(G${r}="Domicilio", J${r}="")`],
      showErrorMessage: true,
      errorStyle: 'stop',
      error: 'Opción no válida, Debe seleccionar tipo de entrega Domicilio',
    }
    if (productosRange) {
      ws.getCell(`N${r}`).dataValidation = {
        type: 'custom',
        allowBlank: true,
        formulae: [`=FALSE`],
        showErrorMessage: true,
        errorStyle: 'stop',
        error:
          'Esta celda se llena automáticamente basado en el nombre de producto.',
      }
    }
    // Lista desplegable para columna M (Nombre de producto)
    if (productosNombresRange) {
      ws.getCell(`M${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [productosNombresRange],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Nombre producto',
        prompt: 'Seleccione nombre del producto',
        errorStyle: 'warning',
        error: 'Valor no válido.',
      }
    }
  }

  // Add borders to H, I, J columns
  for (let r = 1; r <= 1000; r++) {
    ws.getCell(`H${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`I${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`J${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`N${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`M${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
  }

  // Conditional formatting to gray out blocked cells
  ws.addConditionalFormatting({
    ref: 'H2:H1000',
    rules: [
      {
        type: 'expression',
        formulae: ['G2<>"Oficina"'],
        priority: 1,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })
  ws.addConditionalFormatting({
    ref: 'I2:I1000',
    rules: [
      {
        type: 'expression',
        formulae: ['G2<>"Domicilio"'],
        priority: 2,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })
  ws.addConditionalFormatting({
    ref: 'J2:J1000',
    rules: [
      {
        type: 'expression',
        formulae: ['G2<>"Domicilio"'],
        priority: 3,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })
  // Gray out column N (read-only)
  ws.addConditionalFormatting({
    ref: 'M2:M1000',
    rules: [
      {
        type: 'expression',
        formulae: ['TRUE'],
        priority: 4,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })

  // Auto-populate product code (col N) from selected product name (col M)
  for (let r = 2; r <= 1000; r++) {
    // Si hay nombre en M, buscar código correspondiente en N usando INDEX/MATCH
    ws.getCell(`N${r}`).value = {
      formula: `IF(M${r}="", "", IFERROR(INDEX(Listas!$A:$A, MATCH(M${r}, Listas!$B:$B, 0)), ""))`,
    }
  }

  // Forzar recálculo completo al abrir en Excel (forma correcta con ExcelJS)
  wb.calcProperties.fullCalcOnLoad = true

  // Set basic column widths for readability
  const widths = [20, 16, 22, 26, 18, 26, 34, 26, 28, 32, 22, 24, 18, 26, 12]
  ws.columns = widths.map((w) => ({ width: w }))

  // Export as a browser download
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const href = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = 'FormatoEnvíoDesdeInventarioV1.xlsx'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(href)
}

export const exportFormatoShipmentXLSX_V2 = async (
  identificationTypes: {
    taxIdentificationTypeId: string | number
    abbreviationName: string
  }[],
  typeShipment: { value: string | number; name: string }[],
  businessUnits: {
    buCode: string
    buName?: string
    buArea?: string
    location?: { address?: string; locationCode?: string }
  }[],
  citiesType: { locationCode: string; locationName: string }[],
  pieces?: PieceCategoryInterface[],
  isCOD?: boolean
) => {
  const wb = new Workbook()
  const ws = wb.addWorksheet('FormatoMasivoV2')

  // Hidden sheet with lists
  const lists = wb.addWorksheet('Listas')
  lists.state = 'hidden'

  lists.getCell('A1').value = 'CodigoProducto'
  lists.getCell('B1').value = 'NombreProducto'
  const genericProducts =
    pieces && pieces.length > 0
      ? pieces.map((p) => ({ code: p.id, name: p.name }))
      : []

  genericProducts.forEach((p, idx) => {
    lists.getCell(`A${idx + 2}`).value = p.code
    lists.getCell(`B${idx + 2}`).value = p.name
  })
  lists.getColumn(1).numFmt = '@'

  lists.getCell('D1').value = 'TaxIdentificationTypeId'
  lists.getCell('E1').value = 'Abreviacion'
  identificationTypes.forEach((t, idx) => {
    lists.getCell(`D${idx + 2}`).value = t.taxIdentificationTypeId
    lists.getCell(`E${idx + 2}`).value = t.abbreviationName
  })

  lists.getCell('G1').value = 'TipoEntregaValor'
  lists.getCell('H1').value = 'TipoEntregaNombre'
  typeShipment.forEach((t, idx) => {
    lists.getCell(`G${idx + 2}`).value = t.value
    lists.getCell(`H${idx + 2}`).value = t.name
  })

  // Store lists and mappings
  lists.getCell('J1').value = 'BuCodeArea' // To be used in Dropdown
  lists.getCell('K1').value = 'BuName'
  lists.getCell('L1').value = 'BuAddress' // For VLOOKUP
  lists.getCell('M1').value = 'BuCityName' // For VLOOKUP

  const sortedBus = [...businessUnits].sort((a, b) =>
    (a.buCode || '').localeCompare(b.buCode || '')
  )

  sortedBus.forEach((b, idx) => {
    const row = idx + 2
    // J: Dropdown Value
    lists.getCell(`J${row}`).value = `${b.buCode} - ${
      b.buArea?.toUpperCase() || b.buCode?.toUpperCase()
    }`
    // K: Name
    lists.getCell(`K${row}`).value = b.buName || ''
    // L: Address
    lists.getCell(`L${row}`).value = b.location?.address || ''
    // M: City Name (mapped from locationCode)
    const city = citiesType.find(
      (c) => c.locationCode === b.location?.locationCode
    )
    lists.getCell(`M${row}`).value = city?.locationName || ''
  })

  // Cities List (Moved to O, P to make space for Store attributes)
  lists.getCell('O1').value = 'CityCode'
  lists.getCell('P1').value = 'CityName'
  citiesType.forEach((c, idx) => {
    lists.getCell(`O${idx + 2}`).value = c.locationCode
    lists.getCell(`P${idx + 2}`).value = c.locationName
  })

  const prodNamesRange = genericProducts.length
    ? `Listas!$B$2:$B$${genericProducts.length + 1}`
    : null
  const docTypesRange = identificationTypes.length
    ? `Listas!$E$2:$E$${identificationTypes.length + 1}`
    : null
  const delivTypesRange = typeShipment.length
    ? `Listas!$H$2:$H$${typeShipment.length + 1}`
    : null
  const storesRange = sortedBus.length
    ? `Listas!$J$2:$J$${sortedBus.length + 1}`
    : 'Listas!$J$1'
  const citiesRange = citiesType.length
    ? `Listas!$P$2:$P$${citiesType.length + 1}`
    : 'Listas!$P$1'
  const emptyRange = `Listas!$Z$1`

  // Headers
  const headers = [
    'Fecha de Creación',
    'Numero de Guía / Tracking aliado',
    'Tipo de entrega (retiro por oficina/domicilio)',
    'Ciudad/Localidad destino (si es domicilio)',
    'Código de tienda destino (si es retiro por oficina)',
    'Tipo de documento Remitente',
    'Nº de Documento Remitente',
    'Nombre Remitente',
    'Tipo de documento destinatario',
    'Nº de Documento destinatario',
    'Nombre destinatario',
    'Telefono destinatario',
    'Correo destinatario',
    'Dirección Destino',
    'Codigo Postal',
    'Observaciones',
    'Nº Pieza',
    'Peso en Kg por pieza',
    'Dimension Alto por pieza',
    'Dimension Ancho por pieza',
    'Dimension Largo por pieza',
    'Valor declarado por pieza',
    'Nombre producto',
    'Código producto',
  ]

  ws.addRow(headers)
  ws.getRow(1).font = { bold: true }

  const columnWidths = [
    20, 35, 30, 35, 35, 35, 35, 30, 35, 35, 30, 30, 30, 40, 18, 30, 12, 18, 25,
    25, 25, 35, 30, 20,
  ]

  ws.columns = headers.map((header, idx) => ({
    header,
    key: `col_${idx + 1}`,
    width: columnWidths[idx],
  }))

  const textColumns = [
    1, 2, 7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 24,
  ]
  for (let r = 2; r <= 1000; r++) {
    textColumns.forEach((colIdx) => {
      ws.getCell(r, colIdx).numFmt = '@'
    })
  }

  // Notes
  ws.getCell('A1').note = 'Formato: Año-Mes-Día'
  ws.getCell('C1').note = 'Seleccione "Oficina" o "Domicilio"'
  ws.getCell('D1').note =
    'Si "Oficina": Se llena automática. Si "Domicilio": Seleccione.'
  ws.getCell('E1').note = 'Solo si entrega es en Oficina'
  ws.getCell('F1').note = isCOD
    ? 'Su línea de Facturación no permite editar estos campos'
    : 'Seleccione Documento del Remitente'
  ws.getCell('G1').note = isCOD
    ? 'Su línea de Facturación no permite editar estos campos'
    : ''
  ws.getCell('H1').note = isCOD
    ? 'Su línea de Facturación no permite editar estos campos'
    : ''
  ws.getCell('I1').note = 'Seleccione Documento del Destinatario'
  ws.getCell('W1').note = 'Seleccione Nombre del Producto'
  ws.getCell('X1').note = 'Autocompletado basado en Nombre del Producto'

  ws.getCell('V1').note = 'El valor declarado es por pieza, no por total.'
  ws.getCell('Q1').note = 'Peso de cada pieza en kg.'
  ws.getCell('R1').note = 'Alto de cada pieza en cm.'
  ws.getCell('S1').note = 'Ancho de cada pieza en cm.'
  ws.getCell('T1').note = 'Largo de cada pieza en cm.'

  for (let r = 2; r <= 1000; r++) {
    // Borders
    ws.getRow(r).eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
        left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
        bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
        right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      }
    })

    // Force date format as Text (YYYY-MM-DD)
    const cellA = ws.getCell(`A${r}`)
    cellA.numFmt = '@'
    cellA.dataValidation = {
      type: 'textLength',
      allowBlank: true,
      operator: 'equal',
      formulae: [10],
      showErrorMessage: true,
      errorTitle: 'Formato inválido',
      error:
        'La fecha debe tener el formato Año-Mes-Día (longitud exacta: 10 caracteres)',
      showInputMessage: true,
      promptTitle: 'Fecha',
      prompt: 'Formato: Año-Mes-Día (Texto)',
    }

    // Data Validation
    if (delivTypesRange)
      ws.getCell(`C${r}`).dataValidation = {
        type: 'list',
        allowBlank: false, // 🔥 CLAVE
        formulae: [delivTypesRange],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'Campo obligatorio',
        error: 'Debe seleccionar un tipo de entrega.',
      }

    // City: Validation depending on type
    ws.getCell(`D${r}`).dataValidation = {
      type: 'list',
      allowBlank: true, // Important to allow empty string from formula
      formulae: [
        `IF(ISNUMBER(SEARCH("Domicilio", $C${r})), ${citiesRange}, ${emptyRange})`,
      ],
      showErrorMessage: true,
      errorTitle: 'Entrada Inválida',
      error: 'Ciudad solo editable para "Domicilio". En Oficina es automático.',
    }

    // Auto-fill City if Oficina
    ws.getCell(`D${r}`).value = {
      formula: `IF(ISNUMBER(SEARCH("Oficina", C${r})), IFERROR(VLOOKUP(E${r}, Listas!J:M, 4, FALSE), ""), "")`,
    }

    ws.getCell(`E${r}`).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`IF($C${r}="Oficina", ${storesRange}, ${emptyRange})`],
    }

    if (docTypesRange) {
      if (isCOD) {
        // Lock F, G, H for COD
        ws.getCell(`F${r}`).protection = { locked: true }
        ws.getCell(`F${r}`).dataValidation = {
          type: 'custom',
          formulae: ['FALSE'],
          showErrorMessage: true,
          errorStyle: 'stop',
          errorTitle: 'Campo bloqueado',
          error: 'Su línea de Facturación no permite editar estos campos',
        }
        ws.getCell(`G${r}`).protection = { locked: true }
        ws.getCell(`G${r}`).dataValidation = {
          type: 'custom',
          formulae: ['FALSE'],
          showErrorMessage: true,
          errorStyle: 'stop',
          errorTitle: 'Campo bloqueado',
          error: 'Su línea de Facturación no permite editar estos campos',
        }
        ws.getCell(`H${r}`).protection = { locked: true }
        ws.getCell(`H${r}`).dataValidation = {
          type: 'custom',
          formulae: ['FALSE'],
          showErrorMessage: true,
          errorStyle: 'stop',
          errorTitle: 'Campo bloqueado',
          error: 'Su línea de Facturación no permite editar estos campos',
        }
      } else {
        ws.getCell(`F${r}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [docTypesRange],
        }
      }
      ws.getCell(`I${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [docTypesRange],
      }
    }

    ws.getCell(`N${r}`).value = {
      formula: `IF(ISNUMBER(SEARCH("Oficina", C${r})), IFERROR(VLOOKUP(E${r}, Listas!J:L, 3, FALSE), ""), "")`,
    }

    ws.getCell(`N${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [`=AND($C${r}<>"", $C${r}="Domicilio")`],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Campo bloqueado',
      error:
        'Debe seleccionar "Domicilio" en Tipo de entrega para ingresar dirección.',
    }

    if (prodNamesRange)
      ws.getCell(`W${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [prodNamesRange],
      }

    // Formula
    ws.getCell(`X${r}`).value = {
      formula: `IF(W${r}="", "", IFERROR(INDEX(Listas!$A:$A, MATCH(W${r}, Listas!$B:$B, 0)), ""))`,
    }

    ws.getCell(`X${r}`).protection = {
      locked: true,
    }
    ws.getCell(`X${r}`).dataValidation = {
      type: 'custom',
      formulae: ['FALSE'],
      showErrorMessage: true,
      errorStyle: 'stop',
      error: 'Esta celda es automática y no se puede editar.',
    }
  }

  // Conditional Formatting
  const readOnlyStyle = {
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } },
  } as any

  ws.addConditionalFormatting({
    ref: 'E2:E1000',
    rules: [
      {
        priority: 1,
        type: 'expression',
        formulae: ['ISERROR(SEARCH("Oficina", $C2))'], // If Not Oficina (e.g. Domicilio)
        style: readOnlyStyle,
      },
    ],
  })

  ws.addConditionalFormatting({
    ref: 'D2:D1000',
    rules: [
      {
        priority: 2,
        type: 'expression',
        formulae: ['ISERROR(SEARCH("Domicilio", $C2))'], // If Not Domicilio (e.g. Oficina)
        style: readOnlyStyle,
      },
    ],
  })

  ws.addConditionalFormatting({
    ref: `N2:N1000`,
    rules: [
      {
        priority: 3,
        type: 'expression',
        // Se pone gris si NO dice "Domicilio"
        formulae: [`ISERROR(SEARCH("Domicilio", $C2))`],
        style: readOnlyStyle,
      },
    ],
  })

  ws.addConditionalFormatting({
    ref: 'X2:X1000',
    rules: [
      {
        priority: 4,
        type: 'expression',
        formulae: ['TRUE'],
        style: readOnlyStyle,
      },
    ],
  })

  if (isCOD) {
    ws.addConditionalFormatting({
      ref: 'F2:H1000',
      rules: [
        {
          priority: 5,
          type: 'expression',
          formulae: ['TRUE'],
          style: readOnlyStyle,
        },
      ],
    })
  }

  wb.calcProperties.fullCalcOnLoad = true

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const href = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = `FormatoCargaMasiva_V2_${moment().format('DDMMYYYYHHmm')}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(href)
}

// Template V2 with coordinates (Q=Latitud, R=Longitud) + F/G favorite destination columns
// for /shipment/bulk-upload. V3: 28 columns (A-AB).
export const exportFormatoShipmentXLSX_V2WithCoords = async (
  identificationTypes: {
    taxIdentificationTypeId: string | number
    abbreviationName: string
  }[],
  typeShipment: { value: string | number; name: string }[],
  businessUnits: {
    buCode: string
    buName?: string
    buArea?: string
    location?: { address?: string; locationCode?: string }
  }[],
  citiesType: { locationCode: string; locationName: string }[],
  pieces?: PieceCategoryInterface[],
  isCOD?: boolean,
  favoriteDestinations: {
    id?: string
    account?: {
      id?: string
      taxIdentificationTypeID?: number
      identificationNumber?: string
      accountFullName?: string
      displayName?: string
      listAccountPhone?: { phoneNumber?: string }[]
      listAccountEmail?: { email?: string }[]
    }
    location?: {
      address?: string
      postalCode?: string
      coordinates?: { lat?: number; lng?: number }
      // H.3a: address hint ("Frente al parque") — used by VLOOKUP into AC.
      reference?: string
    }
    businessUnit?: {
      buCode?: string
      location?: { locationCode?: string }
    }
    // store.code = buCode del backend; presente aun cuando businessUnit queda vacío
    // (favoritos a Domicilio). Se usa como fallback para StoreCode/CiudadCode.
    store?: { code?: string }
    isHomeDelivery?: boolean
  }[] = []
) => {
  const wb = new Workbook()
  const ws = wb.addWorksheet('FormatoMasivoV2')

  // Carga masiva-only display label for the OFFICE delivery type. Internal code stays
  // DeliveryEnum.OFFICE = '10'; this is purely the text the operator sees in the
  // dropdown + the literal every "es tienda?" formula matches against. Single source of
  // truth so the dropdown value, the SEARCH()/IF() formulas, the conditional formatting,
  // and the import normalization can never drift apart.
  const TIENDA_LABEL = 'Tienda'

  const lists = wb.addWorksheet('Listas')
  lists.state = 'hidden'

  lists.getCell('A1').value = 'CodigoProducto'
  lists.getCell('B1').value = 'NombreProducto'
  const genericProducts =
    pieces && pieces.length > 0
      ? pieces.map((p) => ({ code: p.id, name: p.name }))
      : []
  genericProducts.forEach((p, idx) => {
    lists.getCell(`A${idx + 2}`).value = p.code
    lists.getCell(`B${idx + 2}`).value = p.name
  })
  lists.getColumn(1).numFmt = '@'

  lists.getCell('D1').value = 'TaxIdentificationTypeId'
  lists.getCell('E1').value = 'Abreviacion'
  identificationTypes.forEach((t, idx) => {
    lists.getCell(`D${idx + 2}`).value = t.taxIdentificationTypeId
    lists.getCell(`E${idx + 2}`).value = t.abbreviationName
  })

  lists.getCell('G1').value = 'TipoEntregaValor'
  lists.getCell('H1').value = 'TipoEntregaNombre'
  typeShipment.forEach((t, idx) => {
    lists.getCell(`G${idx + 2}`).value = t.value
    // Carga masiva-only: show OFFICE ('10') as "Tienda" instead of its backend name.
    lists.getCell(`H${idx + 2}`).value =
      String(t.value) === String(DeliveryEnum.OFFICE) ? TIENDA_LABEL : t.name
  })

  lists.getCell('J1').value = 'BuCodeArea'
  lists.getCell('K1').value = 'BuName'
  lists.getCell('L1').value = 'BuAddress'
  lists.getCell('M1').value = 'BuCityName'

  const sortedBus = [...businessUnits].sort((a, b) =>
    (a.buCode || '').localeCompare(b.buCode || '')
  )
  // Ciudad mostrada como "Localidad - Código" en todo el flujo (dropdown, autofill por
  // tienda y favorito). El parser de import extrae el código del sufijo.
  const cityDisplay = (locationCode?: string): string => {
    if (!locationCode) return ''
    const c = citiesType.find((ct) => ct.locationCode === locationCode)
    return c ? `${c.locationName} - ${c.locationCode}` : locationCode
  }
  sortedBus.forEach((b, idx) => {
    const row = idx + 2
    lists.getCell(`J${row}`).value = `${b.buCode} - ${
      b.buArea?.toUpperCase() || b.buCode?.toUpperCase()
    }`
    lists.getCell(`K${row}`).value = b.buName || ''
    lists.getCell(`L${row}`).value = b.location?.address || ''
    lists.getCell(`M${row}`).value = cityDisplay(b.location?.locationCode)
  })

  lists.getCell('O1').value = 'CityCode'
  lists.getCell('P1').value = 'CityName'
  citiesType.forEach((c, idx) => {
    lists.getCell(`O${idx + 2}`).value = c.locationCode
    lists.getCell(`P${idx + 2}`).value = `${c.locationName} - ${c.locationCode}`
  })

  // V3: hidden Favoritos sheet with autofill data
  // Layout:
  //   A: Identificador "RIF — Nombre" (dropdown display)
  //   B: TipoDocDestinatario (Abreviación: V, J, E)
  //   C: NºDocDestinatario (just identificationNumber)
  //   D: NombreDestinatario
  //   E: Teléfono
  //   F: Correo
  // VLOOKUP from main sheet G into Favoritos columns 2-6 → auto-fills H-L.
  let favRange: string | null = null
  let favFullRange: string | null = null
  if (favoriteDestinations.length > 0) {
    const favSheet = wb.addWorksheet('Favoritos')
    favSheet.state = 'hidden'
    favSheet.getCell('A1').value = 'Identificador'
    favSheet.getCell('B1').value = 'TipoDocAbbr'
    favSheet.getCell('C1').value = 'NumDoc'
    favSheet.getCell('D1').value = 'Nombre'
    favSheet.getCell('E1').value = 'Telefono'
    favSheet.getCell('F1').value = 'Correo'
    // H.3a: extended columns G-K so VLOOKUP also fills delivery fields M-P + AC
    // when the operator picks a favorite in column G of the main sheet.
    favSheet.getCell('G1').value = 'TipoEntrega' // 'Domicilio' or 'Oficina' (matches typeShipment.name)
    favSheet.getCell('H1').value = 'CiudadCode'  // locationCode (matches dropdown)
    favSheet.getCell('I1').value = 'StoreCode'   // buCode
    favSheet.getCell('J1').value = 'Direccion'
    favSheet.getCell('K1').value = 'Referencia'

    // V3+E security: sanitize backend strings before writing to Excel.
    // Prefix with single-quote any value starting with =, +, -, @, \t, \r so Excel
    // does not interpret it as a formula (CSV/Excel formula injection — OWASP).
    const sanitizeForExcel = (v: string): string => {
      if (!v) return ''
      const first = v.charAt(0)
      if (first === '=' || first === '+' || first === '-' || first === '@' ||
          first === '\t' || first === '\r') {
        return "'" + v
      }
      return v
    }

    favoriteDestinations.forEach((fav, idx) => {
      const row = idx + 2
      const rif = fav.account?.identificationNumber || ''
      const name = fav.account?.accountFullName || ''
      const taxId = fav.account?.taxIdentificationTypeID
      // Look up the abbreviation from identificationTypes by taxId
      const docTypeAbbr =
        taxId != null
          ? identificationTypes.find(
              (t) => String(t.taxIdentificationTypeId) === String(taxId)
            )?.abbreviationName || ''
          : ''
      const phone = fav.account?.listAccountPhone?.[0]?.phoneNumber || ''
      const email = fav.account?.listAccountEmail?.[0]?.email || ''

      // V3+E: prefer displayName (richer label from backend) with fallback to "RIF — Nombre"
      const display = fav.account?.displayName || `${rif} — ${name}`
      // H.3a: extended delivery fields. Match Excel column M dropdown values exactly
      // ('Domicilio' or 'Oficina' from typeShipment.name) so VLOOKUP into M works.
      // For city, store the locationName (matches the N dropdown in the main sheet).
      // The import-side parser already accepts either locationCode OR locationName.
      const tipoEntrega = fav.isHomeDelivery ? 'Domicilio' : TIENDA_LABEL
      // StoreCode: el mapper deja businessUnit vacío en favoritos a Domicilio; el buCode
      // real siempre viene en store.code (= buCode del backend). Fallback en cascada.
      const rawBuCode = fav.businessUnit?.buCode || fav.store?.code || ''
      const buObj = rawBuCode
        ? businessUnits.find((b) => String(b.buCode) === String(rawBuCode))
        : undefined
      // El valor escrito DEBE coincidir EXACTAMENTE con el dropdown de Tienda (Listas!J),
      // que es "buCode - AREA". Si guardamos solo "1127", el autofill de la columna M no
      // coincide con ninguna opción del combo. Replicamos el mismo formato de Listas!J.
      const storeCode = buObj
        ? `${buObj.buCode} - ${buObj.buArea?.toUpperCase() || buObj.buCode?.toUpperCase()}`
        : rawBuCode
      // CiudadCode: tampoco viene en businessUnit para Domicilio, y el favorito no trae
      // un código de ciudad propio. Se deriva del BU de la tienda asignada
      // (location.locationCode), mismo origen que el autofill de Ciudad por Tienda.
      const ciudadCode =
        fav.businessUnit?.location?.locationCode ||
        buObj?.location?.locationCode ||
        ''
      // Ciudad como "Localidad - Código" para coincidir con el dropdown N del main sheet.
      const ciudadName = cityDisplay(ciudadCode) || ciudadCode
      const direccion = fav.location?.address || ''
      const referencia = fav.location?.reference || ''
      favSheet.getCell(`A${row}`).value = sanitizeForExcel(display)
      favSheet.getCell(`B${row}`).value = sanitizeForExcel(docTypeAbbr)
      favSheet.getCell(`C${row}`).value = sanitizeForExcel(rif)
      favSheet.getCell(`D${row}`).value = sanitizeForExcel(name)
      favSheet.getCell(`E${row}`).value = sanitizeForExcel(phone)
      favSheet.getCell(`F${row}`).value = sanitizeForExcel(email)
      favSheet.getCell(`G${row}`).value = sanitizeForExcel(tipoEntrega)
      favSheet.getCell(`H${row}`).value = sanitizeForExcel(ciudadName)
      favSheet.getCell(`I${row}`).value = sanitizeForExcel(storeCode)
      favSheet.getCell(`J${row}`).value = sanitizeForExcel(direccion)
      favSheet.getCell(`K${row}`).value = sanitizeForExcel(referencia)
    })
    favSheet.getColumn(1).numFmt = '@'
    favSheet.getColumn(2).numFmt = '@'
    favSheet.getColumn(3).numFmt = '@'
    favSheet.getColumn(5).numFmt = '@'
    // H.3a: text format for the new columns (esp. StoreCode which might be numeric-looking).
    favSheet.getColumn(8).numFmt = '@'
    favSheet.getColumn(9).numFmt = '@'
    favSheet.getColumn(10).numFmt = '@'
    favSheet.getColumn(11).numFmt = '@'
    favRange = `Favoritos!$A$2:$A$${favoriteDestinations.length + 1}`
    // H.3a: extended range A:K (11 columns) for VLOOKUPs in M-P + AC.
    favFullRange = `Favoritos!$A$2:$K$${favoriteDestinations.length + 1}`
  }

  const prodNamesRange = genericProducts.length
    ? `Listas!$B$2:$B$${genericProducts.length + 1}`
    : null
  const docTypesRange = identificationTypes.length
    ? `Listas!$E$2:$E$${identificationTypes.length + 1}`
    : null
  const delivTypesRange = typeShipment.length
    ? `Listas!$H$2:$H$${typeShipment.length + 1}`
    : null
  const storesRange = sortedBus.length
    ? `Listas!$J$2:$J$${sortedBus.length + 1}`
    : 'Listas!$J$1'
  const citiesRange = citiesType.length
    ? `Listas!$P$2:$P$${citiesType.length + 1}`
    : 'Listas!$P$1'
  const emptyRange = `Listas!$Z$1`

  // V4 column order: 28 columns (A-AB). Fecha de creación eliminada; Ciudad↔Tienda
  // intercambiadas (Tienda justo después de Tipo de entrega, luego Ciudad, luego lat/lng).
  // Los headers DEBEN coincidir exactamente con REQUIRED_HEADERS/toSemantic del parser
  // (ShipmentBulkUpload.tsx) — el import mapea por texto de header, no por letra.
  const headers = [
    'Número de guía aliada',                             // A (was B)
    'Tipo de documento Remitente (Opcional)',            // B (was C)
    'Nº de Documento Remitente (Opcional)',              // C (was D)
    'Nombre Remitente (Opcional)',                       // D (was E)
    '¿Es destinatario favorito? (Sí/No)',                // E (was F)
    'Identificador destinatario favorito',               // F (was G)
    'Tipo de documento destinatario',                    // G (was H)
    'Nº de Documento destinatario',                      // H (was I)
    'Nombre destinatario',                               // I (was J)
    'Telefono destinatario',                             // J (was K)
    'Correo destinatario',                               // K (was L)
    'Tipo de entrega',                                   // L (was M)
    'Código de tienda destino (si es entrega en tienda)', // M (was O — swap)
    'Ciudad/Localidad destino (si es domicilio)',         // N (was N)
    'Latitud destino (si es domicilio)',                  // O (was P)
    'Longitud destino (si es domicilio)',                 // P (was Q)
    'Dirección Destino',                                  // Q (was R)
    'Punto de referencia (Opcional)',                     // R (was S)
    'Codigo Postal (Opcional)',                           // S (was T)
    'Categoria del contenido',                            // T (was U)
    'Código de categoría',                                // U (was V — OCULTA)
    'Cantidad de Piezas',                                 // V (was W)
    'Peso en Kg por pieza',                              // W (was X)
    'Dimension Alto por pieza',                          // X (was Y)
    'Dimension Ancho por pieza',                         // Y (was Z)
    'Dimension Largo por pieza',                         // Z (was AA)
    'Valor declarado por pieza',                         // AA (was AB)
    'Observaciones',                                     // AB (was AC)
  ]

  // Mapa semántico→letra. Se usa en TODA referencia de celda, fórmula y conditional
  // formatting de aquí en adelante para que el reletra no se desincronice.
  const COL = {
    tracking: 'A',
    remitTipoDoc: 'B', remitNumDoc: 'C', remitNombre: 'D',
    favSiNo: 'E', favId: 'F',
    destTipoDoc: 'G', destNumDoc: 'H', destNombre: 'I', destTel: 'J', destCorreo: 'K',
    tipoEntrega: 'L', tienda: 'M', ciudad: 'N', lat: 'O', lng: 'P',
    direccion: 'Q', puntoRef: 'R', codPostal: 'S',
    categoria: 'T', codCategoria: 'U', cantPiezas: 'V',
    peso: 'W', alto: 'X', ancho: 'Y', largo: 'Z', valor: 'AA', observ: 'AB',
  } as const

  ws.addRow(headers)

  // V3+E FINDING-014 (design-review round 2): env-aware Excel header palette.
  // Mirrors the Tailwind main-* scheme defined in tailwind.config.js:
  // - Production: Tealca Blue scale  → header fill #C5E4F8 (main-100), border #0072C9 (main-400)
  // - Development: Red scale          → header fill #FEE2E2 (main-100), border #e53e3e (main-500)
  // Single source of truth = REACT_APP_ENV. Operators downloading a template in
  // dev get a visual signal "you exported this from a dev env, not prod".
  const isProd = process.env.REACT_APP_ENV === 'production'
  const headerFillArgb = isProd ? 'FFC5E4F8' : 'FFFEE2E2'
  const headerBorderArgb = isProd ? 'FF0072C9' : 'FFE53E3E'
  const headerBorder = {
    top: { style: 'thin' as const, color: { argb: headerBorderArgb } },
    left: { style: 'thin' as const, color: { argb: headerBorderArgb } },
    bottom: { style: 'thin' as const, color: { argb: headerBorderArgb } },
    right: { style: 'thin' as const, color: { argb: headerBorderArgb } },
  }
  ws.getRow(1).font = { bold: true, color: { argb: 'FF1F2937' } }
  ws.getRow(1).height = 28
  ws.getRow(1).eachCell((cell) => {
    cell.protection = { locked: true }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: headerFillArgb },
    }
    cell.border = headerBorder
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  })

  // V4 widths (28 cols A..AB), matched to the new order:
  // A tracking 35, B-D remit 35/35/30, E favSiNo 22, F favId 40,
  // G-K dest 35/35/30/22/30, L tipoEntrega 30, M tienda 35, N ciudad 35,
  // O lat 22, P lng 22, Q dir 40, R puntoRef 35, S postal 18, T categoria 30,
  // U codCategoria 20, V cantPiezas 18, W peso 18, X-Z dims 22, AA valor 25, AB observ 30.
  const columnWidths = [
    35, 35, 35, 30, 22, 40, 35, 35, 30, 22, 30, 30, 35, 35,
    22, 22, 40, 35, 18, 30, 20, 18, 18, 22, 22, 22, 25, 30,
  ]
  ws.columns = headers.map((header, idx) => ({
    header,
    key: `col_${idx + 1}`,
    width: columnWidths[idx],
  }))

  // Text-format columns (1-indexed):
  //   A=1 tracking, C=3 remitNumDoc, D=4 remitNombre, E=5 favSiNo, F=6 favId,
  //   H=8 destNumDoc, I=9 destNombre, J=10 destTel, K=11 destCorreo,
  //   O=15 lat, P=16 lng (TEXTO: evita que Excel meta puntos de miles al pegar),
  //   Q=17 dir, R=18 puntoRef, S=19 postal, T=20 categoria, AB=28 observaciones.
  // Dropdowns (B,G,L,M,N) quedan en formato general; U=codCategoria es fórmula auto.
  const textColumns = [1, 3, 4, 5, 6, 8, 9, 10, 11, 15, 16, 17, 18, 19, 20, 28]

  // Remitente (B/C/D): bloqueados si COD.
  ws.getCell(`${COL.remitTipoDoc}1`).note = isCOD
    ? 'Su línea de Facturación no permite editar estos campos'
    : 'Seleccione Documento del Remitente (Opcional)'
  ws.getCell(`${COL.remitNumDoc}1`).note = isCOD
    ? 'Su línea de Facturación no permite editar estos campos'
    : ''
  ws.getCell(`${COL.remitNombre}1`).note = isCOD
    ? 'Su línea de Facturación no permite editar estos campos'
    : ''
  // E — ¿Es favorito? (con coletilla sobre cómo crear/modificar favoritos)
  ws.getCell(`${COL.favSiNo}1`).note =
    'Seleccione "Sí" para usar un destinatario favorito (autocompletará G-K y tipo de entrega/tienda/ciudad/dirección/punto de referencia). Si cambia de "Sí" a "No", recuerde borrar manualmente el valor de la columna F (quedará marcado en rojo para recordarlo). Para crear o modificar un destinatario favorito ve al menú Configuraciones > Destinos Favoritos: verás la lista de los creados y un botón para crear uno nuevo.'
  ws.getCell(`${COL.favId}1`).note =
    favoriteDestinations.length > 0
      ? 'Seleccione el favorito. Solo activo si la columna E = "Sí" — el dropdown estará vacío en otro caso. Si cambió E de "Sí" a "No", borre manualmente esta celda. Al seleccionar, los datos del destinatario y entrega se autocompletan.'
      : 'Sin destinatarios favoritos guardados. Esta columna se mantiene como texto libre.'
  ws.getCell(`${COL.destTipoDoc}1`).note =
    favoriteDestinations.length > 0
      ? 'Si E = "Sí": autocompletado desde el favorito. Si no: seleccione Documento del Destinatario.'
      : 'Seleccione Documento del Destinatario'
  // J — Teléfono: operadora + 7 dígitos
  ws.getCell(`${COL.destTel}1`).note =
    'Operadora (412, 414, 416, 422, 424, 426) o código de teléfono fijo (ej. 0212) seguido de 7 dígitos = 10 dígitos en total.'
  // K — Correo
  ws.getCell(`${COL.destCorreo}1`).note = 'Formato: usuario@dominio.com'
  ws.getCell(`${COL.tipoEntrega}1`).note = `Seleccione "${TIENDA_LABEL}" o "Domicilio"`
  ws.getCell(`${COL.tienda}1`).note = `Solo si la entrega es en ${TIENDA_LABEL}`
  ws.getCell(`${COL.ciudad}1`).note = `Si "${TIENDA_LABEL}": Se llena automática. Si "Domicilio": Seleccione.`
  ws.getCell(`${COL.lat}1`).note = 'Solo completar si Tipo de entrega = Domicilio. Ejemplo: 10.4806 (formato Texto).'
  ws.getCell(`${COL.lng}1`).note = 'Solo completar si Tipo de entrega = Domicilio. Ejemplo: -66.9147 (formato Texto).'
  ws.getCell(`${COL.puntoRef}1`).note = 'Punto de referencia opcional (ej: "Frente al parque"). Solo aplica a entregas a Domicilio.'
  ws.getCell(`${COL.categoria}1`).note = 'Seleccione la Categoría del contenido'
  ws.getCell(`${COL.codCategoria}1`).note = 'Autocompletado en base a la Categoría del contenido'
  ws.getCell(`${COL.cantPiezas}1`).note = 'Todas las piezas tendrán el mismo valor de Peso, dimensiones y categoría.'
  ws.getCell(`${COL.valor}1`).note = 'El valor declarado es por pieza, no por total.'

  // V3+E: uniform borders + white fill on the data area.
  // PERF: Apply border/fill/protection via column-level style (one mutation per column
  // = 28 mutations) instead of per-cell loop (27,972 mutations). ExcelJS preserves
  // cell-specific styles (like the header row set above) over column defaults.
  // Note: column.style applies to ALL rows in the column; header row 1 already has
  // explicit border/fill/protection set so it overrides correctly.
  const dataBorder = {
    top: { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
    left: { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
    bottom: { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
    right: { style: 'thin' as const, color: { argb: 'FFCBD5E1' } },
  }
  const dataFill = {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'FFFFFFFF' },
  }
  const TOTAL_COLS = headers.length // 28 (A..AB)

  for (let c = 1; c <= TOTAL_COLS; c++) {
    const col = ws.getColumn(c)
    col.border = dataBorder
    col.fill = dataFill
    col.protection = { locked: false }
  }

  // Re-apply header-row styles AFTER column.style so header overrides column defaults
  // (ExcelJS sometimes resets cell styles when column.style is assigned).
  // Uses the same env-aware palette as the initial header pass (FINDING-014).
  ws.getRow(1).eachCell((cell) => {
    cell.protection = { locked: true }
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: headerFillArgb },
    }
    cell.border = headerBorder
    cell.font = { bold: true, color: { argb: 'FF1F2937' } }
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  })

  // Mensaje de bloqueo cuando E="Sí": las celdas autocompletadas del favorito no se editan.
  // Para las celdas con dropdown, el mensaje también cubre el caso "valor fuera de lista".
  const FAV_LOCK_ERR =
    'No se puede modificar la información porque son datos de un destinatario favorito.'
  const FAV_LOCK_LIST_ERR =
    FAV_LOCK_ERR + ' Si no es favorito, seleccione un valor de la lista.'
  // Mensaje para los campos que solo aplican a entrega a Domicilio (Ciudad, Latitud,
  // Longitud, Dirección, Punto de referencia, Código Postal). Se bloquean cuando el tipo
  // de entrega es Tienda (y también cuando es un destinatario favorito, que los autocompleta).
  const NO_DOMICILIO_ERR =
    'No se puede modificar porque el tipo de entrega no es a Domicilio (o es un destinatario favorito).'

  for (let r = 2; r <= 1000; r++) {

    textColumns.forEach((colIdx) => {
      ws.getCell(r, colIdx).numFmt = '@'
    })

    // B/C/D — Remitente (COD: locked; else: doc type dropdown for B)
    if (docTypesRange) {
      if (isCOD) {
        ;[COL.remitTipoDoc, COL.remitNumDoc, COL.remitNombre].forEach((col) => {
          ws.getCell(`${col}${r}`).protection = { locked: true }
          ws.getCell(`${col}${r}`).dataValidation = {
            type: 'custom',
            formulae: ['FALSE'],
            showErrorMessage: true,
            errorStyle: 'stop',
            errorTitle: 'Campo bloqueado',
            error: 'Su línea de Facturación no permite editar estos campos',
          }
        })
      } else {
        ws.getCell(`${COL.remitTipoDoc}${r}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [docTypesRange],
        }
      }
      // G — TipoDocDestinatario dropdown. Dinámico: lista vacía cuando E="Sí" (favorito),
      // de modo que cualquier intento de escribir se rechaza con el mensaje de bloqueo.
      ws.getCell(`${COL.destTipoDoc}${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`IF($${COL.favSiNo}${r}="Sí", ${emptyRange}, ${docTypesRange})`],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'Bloqueado',
        error: FAV_LOCK_LIST_ERR,
      }
    }

    // E — ¿Es destinatario favorito? (Sí/No)
    ws.getCell(`${COL.favSiNo}${r}`).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: ['"Sí,No"'],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Valor inválido',
      error: 'Seleccione Sí o No',
    }

    // F — Identificador destinatario favorito. Dropdown dinámico: muestra favoritos solo
    // cuando E="Sí", vacío en otro caso.
    if (favRange) {
      ws.getCell(`${COL.favId}${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`IF($${COL.favSiNo}${r}="Sí", ${favRange}, ${emptyRange})`],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'E debe ser "Sí"',
        error: 'Para seleccionar un favorito, primero coloque "Sí" en la columna E. Si cambió E a "No", borre el valor de F.',
      }
    }

    // G-K — Autofill destinatario cuando E=Sí vía VLOOKUP en Favoritos.
    // Cols Favoritos: A=Identificador, B=TipoDocAbbr, C=NumDoc, D=Nombre, E=Telefono, F=Correo
    if (favFullRange) {
      const favLookup = (col: number) =>
        `IF($${COL.favSiNo}${r}="Sí", IFERROR(VLOOKUP($${COL.favId}${r}, ${favFullRange}, ${col}, FALSE), ""), "")`
      ws.getCell(`${COL.destTipoDoc}${r}`).value = { formula: favLookup(2) }
      ws.getCell(`${COL.destNumDoc}${r}`).value = { formula: favLookup(3) }
      ws.getCell(`${COL.destNombre}${r}`).value = { formula: favLookup(4) }
      ws.getCell(`${COL.destTel}${r}`).value = { formula: favLookup(5) }
      ws.getCell(`${COL.destCorreo}${r}`).value = { formula: favLookup(6) }
    }

    // Bloqueo de los campos de texto libre del destinatario (H, I, J, K) cuando E="Sí":
    // son datos del favorito y no deben editarse. Validación custom que solo permite
    // escribir si E≠"Sí" (la fórmula de autocompletado rellena el valor en el caso "Sí").
    ;[COL.destNumDoc, COL.destNombre, COL.destTel, COL.destCorreo].forEach((c) => {
      ws.getCell(`${c}${r}`).dataValidation = {
        type: 'custom',
        allowBlank: true,
        formulae: [`=$${COL.favSiNo}${r}<>"Sí"`],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'Bloqueado',
        error: FAV_LOCK_ERR,
      }
    })

    // L — TipoEntrega dropdown. Autofill desde favorito (col 7) cuando E=Sí.
    if (delivTypesRange)
      ws.getCell(`${COL.tipoEntrega}${r}`).dataValidation = {
        type: 'list',
        allowBlank: false,
        // Lista vacía cuando E="Sí": el tipo de entrega viene del favorito, no se edita.
        formulae: [`IF($${COL.favSiNo}${r}="Sí", ${emptyRange}, ${delivTypesRange})`],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'Tipo de entrega',
        error: FAV_LOCK_LIST_ERR,
      }
    if (favFullRange) {
      ws.getCell(`${COL.tipoEntrega}${r}`).value = {
        formula: `IF($${COL.favSiNo}${r}="Sí", IFERROR(VLOOKUP($${COL.favId}${r}, ${favFullRange}, 7, FALSE), ""), "")`,
      }
    }

    // M — CodTienda dropdown (solo cuando Tipo de entrega = Tienda). Autofill favorito col 9.
    ws.getCell(`${COL.tienda}${r}`).dataValidation = {
      type: 'list',
      allowBlank: true,
      // Vacía (bloqueada) cuando E="Sí" (favorito) o cuando no es entrega en Tienda.
      formulae: [
        `IF($${COL.favSiNo}${r}="Sí", ${emptyRange}, IF($${COL.tipoEntrega}${r}="${TIENDA_LABEL}", ${storesRange}, ${emptyRange}))`,
      ],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Código de tienda',
      error: FAV_LOCK_LIST_ERR,
    }
    if (favFullRange) {
      ws.getCell(`${COL.tienda}${r}`).value = {
        formula: `IF($${COL.favSiNo}${r}="Sí", IFERROR(VLOOKUP($${COL.favId}${r}, ${favFullRange}, 9, FALSE), ""), "")`,
      }
    }

    // N — Ciudad (dropdown + autofill). Prioridad: favorito (col 8) gana; si no, derivada
    // de la tienda (VLOOKUP de la tienda M en Listas J:M col 4) cuando entrega = Tienda.
    ws.getCell(`${COL.ciudad}${r}`).dataValidation = {
      type: 'list',
      allowBlank: true,
      // Vacía (bloqueada) cuando E="Sí" (favorito) o cuando no es Domicilio.
      formulae: [
        `IF($${COL.favSiNo}${r}="Sí", ${emptyRange}, IF(ISNUMBER(SEARCH("Domicilio", $${COL.tipoEntrega}${r})), ${citiesRange}, ${emptyRange}))`,
      ],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Ciudad',
      error: NO_DOMICILIO_ERR,
    }
    ws.getCell(`${COL.ciudad}${r}`).value = favFullRange
      ? {
          formula: `IF($${COL.favSiNo}${r}="Sí", IFERROR(VLOOKUP($${COL.favId}${r}, ${favFullRange}, 8, FALSE), ""), IF(ISNUMBER(SEARCH("${TIENDA_LABEL}", ${COL.tipoEntrega}${r})), IFERROR(VLOOKUP(${COL.tienda}${r}, Listas!J:M, 4, FALSE), ""), ""))`,
        }
      : {
          formula: `IF(ISNUMBER(SEARCH("${TIENDA_LABEL}", ${COL.tipoEntrega}${r})), IFERROR(VLOOKUP(${COL.tienda}${r}, Listas!J:M, 4, FALSE), ""), "")`,
        }

    // O — Latitud (TEXTO). Editable solo en Domicilio sin favorito; bloqueada en Tienda.
    ws.getCell(`${COL.lat}${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [`=AND($${COL.tipoEntrega}${r}="Domicilio", $${COL.favSiNo}${r}<>"Sí")`],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Solo Domicilio',
      error: NO_DOMICILIO_ERR,
      showInputMessage: true,
      promptTitle: 'Latitud',
      prompt: 'Ejemplo: 10.4806 (acepta punto o coma como separador decimal). Solo para Domicilio.',
    }

    // P — Longitud (TEXTO). Misma regla que Latitud.
    ws.getCell(`${COL.lng}${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [`=AND($${COL.tipoEntrega}${r}="Domicilio", $${COL.favSiNo}${r}<>"Sí")`],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Solo Domicilio',
      error: NO_DOMICILIO_ERR,
      showInputMessage: true,
      promptTitle: 'Longitud',
      prompt: 'Ejemplo: -66.9147 (acepta punto o coma como separador decimal). Solo para Domicilio.',
    }

    // Q — Dirección Destino. AUTOFILL: al elegir la Tienda (M), la dirección se rellena
    // automáticamente con la del BU vía VLOOKUP(M, Listas!J:L, 3) — funciona porque M
    // guarda exactamente "buCode - AREA" igual que Listas!J. Prioridad: favorito (col 10)
    // gana; si no, derivada de la tienda; en Domicilio queda vacía para que el usuario la
    // complete (o la trae Maps en el editor).
    ws.getCell(`${COL.direccion}${r}`).value = favFullRange
      ? {
          formula: `IF($${COL.favSiNo}${r}="Sí", IFERROR(VLOOKUP($${COL.favId}${r}, ${favFullRange}, 10, FALSE), ""), IF(ISNUMBER(SEARCH("${TIENDA_LABEL}", ${COL.tipoEntrega}${r})), IFERROR(VLOOKUP(${COL.tienda}${r}, Listas!J:L, 3, FALSE), ""), ""))`,
        }
      : {
          formula: `IF(ISNUMBER(SEARCH("${TIENDA_LABEL}", ${COL.tipoEntrega}${r})), IFERROR(VLOOKUP(${COL.tienda}${r}, Listas!J:L, 3, FALSE), ""), "")`,
        }
    ws.getCell(`${COL.direccion}${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      // Editable solo si es Domicilio Y NO es favorito (el favorito trae su dirección).
      formulae: [
        `=AND($${COL.tipoEntrega}${r}="Domicilio", $${COL.favSiNo}${r}<>"Sí")`,
      ],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Solo Domicilio',
      error: NO_DOMICILIO_ERR,
    }

    // R — Punto de referencia. Autofill desde favorito (col 11). Editable libre si E≠Sí.
    if (favFullRange) {
      ws.getCell(`${COL.puntoRef}${r}`).value = {
        formula: `IF($${COL.favSiNo}${r}="Sí", IFERROR(VLOOKUP($${COL.favId}${r}, ${favFullRange}, 11, FALSE), ""), "")`,
      }
    }
    // R — Punto de referencia: solo aplica a Domicilio sin favorito.
    ws.getCell(`${COL.puntoRef}${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [`=AND($${COL.tipoEntrega}${r}="Domicilio", $${COL.favSiNo}${r}<>"Sí")`],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Solo Domicilio',
      error: NO_DOMICILIO_ERR,
    }

    // S — Código Postal: solo aplica a Domicilio sin favorito (en Tienda no se usa).
    ws.getCell(`${COL.codPostal}${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [`=AND($${COL.tipoEntrega}${r}="Domicilio", $${COL.favSiNo}${r}<>"Sí")`],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Solo Domicilio',
      error: NO_DOMICILIO_ERR,
    }

    // T — Categoria del contenido (dropdown).
    if (prodNamesRange)
      ws.getCell(`${COL.categoria}${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [prodNamesRange],
      }

    // U — Código de categoría (auto-formula, oculta, read-only).
    ws.getCell(`${COL.codCategoria}${r}`).value = {
      formula: `IF(${COL.categoria}${r}="", "", IFERROR(INDEX(Listas!$A:$A, MATCH(${COL.categoria}${r}, Listas!$B:$B, 0)), ""))`,
    }
    ws.getCell(`${COL.codCategoria}${r}`).protection = { locked: true }
    ws.getCell(`${COL.codCategoria}${r}`).dataValidation = {
      type: 'custom',
      formulae: ['FALSE'],
      showErrorMessage: true,
      errorStyle: 'stop',
      error: 'Esta celda es automática y no se puede editar.',
    }

    // V — Cantidad de Piezas, entero ≥ 1.
    ws.getCell(`${COL.cantPiezas}${r}`).dataValidation = {
      type: 'whole',
      allowBlank: true,
      operator: 'greaterThanOrEqual',
      formulae: [1],
      showErrorMessage: true,
      errorTitle: 'Cantidad inválida',
      error: 'La cantidad debe ser un número entero mayor o igual a 1.',
      showInputMessage: true,
      promptTitle: 'Cantidad de piezas',
      prompt: 'Ingrese un número entero ≥ 1. Si está vacío, se tomará como 1. Todas las piezas tendrán el mismo peso, dimensiones y categoría.',
    }
  }

  const readOnlyStyle = {
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } },
  } as any

  // M — Tienda grayed si no es Tienda
  ws.addConditionalFormatting({
    ref: `${COL.tienda}2:${COL.tienda}1000`,
    rules: [{ priority: 1, type: 'expression', formulae: [`ISERROR(SEARCH("${TIENDA_LABEL}", $${COL.tipoEntrega}2))`], style: readOnlyStyle }],
  })
  // N — Ciudad grayed si no es Domicilio
  ws.addConditionalFormatting({
    ref: `${COL.ciudad}2:${COL.ciudad}1000`,
    rules: [{ priority: 2, type: 'expression', formulae: [`ISERROR(SEARCH("Domicilio", $${COL.tipoEntrega}2))`], style: readOnlyStyle }],
  })
  // Q — Dirección grayed si no es Domicilio
  ws.addConditionalFormatting({
    ref: `${COL.direccion}2:${COL.direccion}1000`,
    rules: [{ priority: 3, type: 'expression', formulae: [`ISERROR(SEARCH("Domicilio", $${COL.tipoEntrega}2))`], style: readOnlyStyle }],
  })
  // U — Código de categoría (auto-formula). Siempre gris.
  ws.addConditionalFormatting({
    ref: `${COL.codCategoria}2:${COL.codCategoria}1000`,
    rules: [{ priority: 4, type: 'expression', formulae: ['TRUE'], style: readOnlyStyle }],
  })
  // O/P — Latitud/Longitud grayed si no es Domicilio
  ws.addConditionalFormatting({
    ref: `${COL.lat}2:${COL.lat}1000`,
    rules: [{ priority: 5, type: 'expression', formulae: [`ISERROR(SEARCH("Domicilio", $${COL.tipoEntrega}2))`], style: readOnlyStyle }],
  })
  ws.addConditionalFormatting({
    ref: `${COL.lng}2:${COL.lng}1000`,
    rules: [{ priority: 6, type: 'expression', formulae: [`ISERROR(SEARCH("Domicilio", $${COL.tipoEntrega}2))`], style: readOnlyStyle }],
  })
  // R — Punto de referencia grayed si no es Domicilio
  ws.addConditionalFormatting({
    ref: `${COL.puntoRef}2:${COL.puntoRef}1000`,
    rules: [{ priority: 6, type: 'expression', formulae: [`ISERROR(SEARCH("Domicilio", $${COL.tipoEntrega}2))`], style: readOnlyStyle }],
  })
  // S — Código Postal grayed si no es Domicilio
  ws.addConditionalFormatting({
    ref: `${COL.codPostal}2:${COL.codPostal}1000`,
    rules: [{ priority: 6, type: 'expression', formulae: [`ISERROR(SEARCH("Domicilio", $${COL.tipoEntrega}2))`], style: readOnlyStyle }],
  })
  // F — Identificador favorito gris si E != "Sí"
  ws.addConditionalFormatting({
    ref: `${COL.favId}2:${COL.favId}1000`,
    rules: [{ priority: 7, type: 'expression', formulae: [`$${COL.favSiNo}2<>"Sí"`], style: readOnlyStyle }],
  })
  // F — Resaltado rojo cuando E!="Sí" pero F tiene un valor viejo (avisa que lo borre)
  const staleWarningStyle = {
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } },
    font: { color: { argb: 'FFB91C1C' }, bold: true },
  } as any
  ws.addConditionalFormatting({
    ref: `${COL.favId}2:${COL.favId}1000`,
    rules: [
      {
        priority: 0,
        type: 'expression',
        formulae: [`AND($${COL.favSiNo}2<>"Sí", $${COL.favId}2<>"")`],
        style: staleWarningStyle,
      },
    ],
  })
  // Campos del destinatario/entrega autocompletados desde el favorito → gris cuando E="Sí".
  // G-K (destinatario) + L-N (tipo entrega/tienda/ciudad) + Q-R (dirección/punto ref).
  // O/P (lat/lng) NO se autocompletan del favorito: se rigen por la regla de Domicilio.
  if (favFullRange) {
    ws.addConditionalFormatting({
      ref: `${COL.destTipoDoc}2:${COL.destCorreo}1000`,
      rules: [{ priority: 9, type: 'expression', formulae: [`$${COL.favSiNo}2="Sí"`], style: readOnlyStyle }],
    })
    ws.addConditionalFormatting({
      ref: `${COL.tipoEntrega}2:${COL.ciudad}1000`,
      rules: [{ priority: 10, type: 'expression', formulae: [`$${COL.favSiNo}2="Sí"`], style: readOnlyStyle }],
    })
    ws.addConditionalFormatting({
      ref: `${COL.direccion}2:${COL.puntoRef}1000`,
      rules: [{ priority: 11, type: 'expression', formulae: [`$${COL.favSiNo}2="Sí"`], style: readOnlyStyle }],
    })
  }
  // COD: B/C/D siempre gris (remitente bloqueado)
  if (isCOD) {
    ws.addConditionalFormatting({
      ref: `${COL.remitTipoDoc}2:${COL.remitNombre}1000`,
      rules: [{ priority: 8, type: 'expression', formulae: ['TRUE'], style: readOnlyStyle }],
    })
  }

  // Ocultar la columna "Código de categoría" — el usuario no debe verla; la fórmula
  // auto sigue calculando y el import la lee aunque esté oculta.
  ws.getColumn(COL.codCategoria).hidden = true

  wb.calcProperties.fullCalcOnLoad = true

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const href = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = `FormatoCargaMasivaV2Coords_${moment().format('DDMMYYYYHHmm')}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(href)
}

//////////////// Export Favorite Destinations
export const exportFavDestinationTXT = (
  favDestinations: FavoriteDestinationsInterfase[]
) => {
  let txt = ''

  for (let i = 0; i < favDestinations.length; i++) {
    const fav = favDestinations[i]

    txt += `"Nombre":"${fav.accountFullName}",\n`
    txt += `"Numero Identificador":${
      fav.taxIDentificationTypeCode + fav.identificationNumber
    },\n`
    txt += `"Teléfono":${fav.phoneNumber},\n`
    txt += `"Email":"${fav.email}",\n`
    txt += `"Dirección":"${fav.addressLine1}",\n`
    txt += i < favDestinations.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaDestinosFavoritos_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

export const exportFavDestinationXLSX = (
  favDestinations: FavoriteDestinationsInterfase[]
) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = favDestinations.map((fav) => [
    fav.accountFullName,
    fav.taxIDentificationTypeCode + fav.identificationNumber,
    fav.phoneNumber,
    fav.email,
    fav.addressLine1,
  ])

  // Add header
  ws_data.unshift([
    'Nombre ',
    'Numero Identificador',
    'Teléfono',
    'Email',
    'Dirección',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaDespacho_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportFavDestinationPDF = (
  user: UserInterface,
  favDestinations: FavoriteDestinationsInterfase[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE DESTINOS FAVORITOS', 80, 25)
  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Nombre ',
    'Numero Identificador',
    'Teléfono',
    'Email',
    'Dirección',
  ]
  const rows = favDestinations.map((fav) => [
    fav.accountFullName,
    fav.taxIDentificationTypeCode + fav.identificationNumber,
    fav.phoneNumber,
    fav.email,
    fav.addressLine1,
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 45, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
      minCellHeight: 11,
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaDestinosFavoritos_${moment().format('DDMMYYYYHHmm')}.pdf`)
}
